import React, { useEffect } from 'react';
import SEO from '../components/SEO';
import SitePage, { styles, T, ACCENT } from '../components/site/SitePage';

// Identité de l'éditeur, exigée par la loi pour la confiance dans l'économie numérique (art. 6) : nom et prénom
// obligatoires pour un entrepreneur individuel, d'où le nom complet ici, et nulle part ailleurs sur le site.
// La page est en noindex pour ne pas remonter sur une recherche du nom.
// SIRET et adresse : à renseigner dès que Mattéo les donne ; tant qu'ils valent null, la ligne n'est pas affichée.
const EDITEUR = {
  nom: 'Mattéo Galfré',
  statut: 'Entrepreneur individuel (micro-entreprise)',
  siret: null,
  adresse: null,
  email: 'hello@autoria.dev'
};

const COOKIES = [
  ['_ga, _ga_*', 'Google Analytics', "Compter les visites, savoir quelles pages sont lues et quels boutons mènent à un rendez-vous", '13 mois'],
  ['autoria-consent', 'Ce site', 'Retenir votre choix sur les cookies, sans quoi ce bandeau reviendrait à chaque page', '6 mois']
];

const H2 = ({ children, id }) => <h2 id={id} style={{ ...styles.h2, fontSize: 'clamp(22px, 2.4vw, 28px)', marginTop: 48 }}>{children}</h2>;
const P = ({ children }) => <p style={{ margin: '12px 0 0', fontSize: 17, color: T.text, lineHeight: 1.6 }}>{children}</p>;
const Line = ({ label, children }) => (
  <p style={{ margin: '8px 0 0', fontSize: 17, color: T.text }}><span style={{ fontWeight: 600, color: T.ink }}>{label} : </span>{children}</p>
);

const MentionsLegalesPage = () => {
  useEffect(() => {
    if (!window.location.hash) window.scrollTo(0, 0);
  }, []);

  return (
    <SitePage>
      <SEO title="Mentions légales et confidentialité" description="Éditeur, hébergeur, données personnelles et cookies du site autoria.dev." path="/mentions-legales" noindex />
      <article style={{ ...styles.narrow, padding: '56px 24px 24px' }}>
        <p style={styles.kicker}>Informations légales</p>
        <h1 style={{ ...styles.h1, marginTop: 14 }}>Mentions légales et confidentialité</h1>

        <H2>Éditeur du site</H2>
        <Line label="Nom">{EDITEUR.nom}</Line>
        <Line label="Statut">{EDITEUR.statut}</Line>
        {EDITEUR.siret && <Line label="SIRET">{EDITEUR.siret}</Line>}
        {EDITEUR.adresse && <Line label="Adresse">{EDITEUR.adresse}</Line>}
        <Line label="Contact"><a href={`mailto:${EDITEUR.email}`}>{EDITEUR.email}</a></Line>
        <Line label="Directeur de la publication">{EDITEUR.nom}</Line>

        <H2>Hébergement</H2>
        <P>Cloudflare, Inc., 101 Townsend St, San Francisco, CA 94107, États-Unis. <a href="https://www.cloudflare.com" rel="noopener noreferrer">cloudflare.com</a>. Cet hébergement concerne ce site. Les outils des clients sont hébergés en Europe, sur un compte à leur nom.</P>

        <H2>Propriété intellectuelle</H2>
        <P>Les textes, visuels et la vidéo de ce site sont la propriété de {EDITEUR.nom}. Les démonstrations utilisent des données fictives : les produits, chiffres et personnes montrés n'existent pas.</P>

        <H2>Données personnelles</H2>
        <P>Le responsable du traitement est {EDITEUR.nom}. Le site ne demande aucune inscription. Des données personnelles ne sont traitées que dans cinq cas :</P>
        <ul style={{ margin: '12px 0 0', paddingLeft: 20, listStyle: 'disc', fontSize: 17, color: T.text, lineHeight: 1.7 }}>
          <li><strong>Vous m'écrivez</strong> : votre adresse et votre message servent à vous répondre, et sont conservés trois ans après notre dernier échange.</li>
          <li><strong>Vous remplissez le questionnaire</strong> : vos réponses, votre adresse et, si vous les donnez, votre prénom et votre entreprise me sont envoyés par e-mail pour vous répondre. Ils sont conservés trois ans après notre dernier échange.</li>
          <li><strong>Vous demandez le modèle Excel</strong> du calculateur de stock : votre adresse, votre prénom si vous le donnez, et les chiffres saisis servent à vous envoyer le fichier, par Brevo, prestataire d'envoi d'e-mails établi en France. Je peux vous écrire une fois pour savoir s'il vous a servi, jamais plus. Ces données sont conservées trois ans après notre dernier échange.</li>
          <li><strong>Vous réservez un appel</strong> : la prise de rendez-vous passe par Calendly, qui traite vos nom, adresse et réponses selon sa propre politique de confidentialité.</li>
          <li><strong>Vous acceptez la mesure d'audience</strong> : voir la partie cookies ci-dessous.</li>
        </ul>
        <P>Certains de ces prestataires (Cloudflare, Calendly, Google) sont établis aux États-Unis. Les transferts sont encadrés par les garanties prévues par le RGPD : Data Privacy Framework ou clauses contractuelles types de la Commission européenne.</P>
        <P>Vous pouvez demander l'accès à vos données, leur rectification, leur effacement ou leur portabilité, et vous opposer à leur traitement, en écrivant à <a href={`mailto:${EDITEUR.email}`}>{EDITEUR.email}</a>. En cas de désaccord, vous pouvez saisir la CNIL (<a href="https://www.cnil.fr" rel="noopener noreferrer">cnil.fr</a>).</P>

        <H2 id="cookies">Cookies</H2>
        <P>La mesure d'audience ne se lance qu'avec votre accord, donné ou refusé dans le bandeau qui s'affiche à la première visite. Sans accord, aucun cookie de mesure n'est déposé. Votre choix est gardé six mois. Seule exception : l'hébergeur, Cloudflare, compte les pages vues sans cookie ni identifiant (Cloudflare Web Analytics). Ces statistiques sont agrégées et ne permettent pas de vous reconnaître.</P>
        <div style={{ marginTop: 20, border: `1px solid ${T.line2}`, borderRadius: 6, overflow: 'hidden', background: '#FFFFFF' }}>
          {COOKIES.map(([name, service, purpose, duration], i) => (
            <div key={name} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))', gap: '4px 16px', padding: '14px 16px', borderTop: i ? `1px solid ${T.line}` : 0, fontSize: 15 }}>
              <span style={{ fontFamily: T.mono, fontSize: 13 }}>{name}</span>
              <span style={{ fontWeight: 600 }}>{service}</span>
              <span style={{ color: T.text, gridColumn: 'span 2' }}>{purpose}</span>
              <span style={{ fontFamily: T.mono, fontSize: 13, color: T.faint }}>{duration}</span>
            </div>
          ))}
        </div>
        <p style={{ margin: '20px 0 0' }}>
          <button type="button" data-cookies="" className="lift" style={{ ...styles.button, border: 0, cursor: 'pointer', fontSize: 16, minHeight: 46, padding: '10px 18px', background: ACCENT }}>Modifier mon choix</button>
        </p>
      </article>
    </SitePage>
  );
};

export default MentionsLegalesPage;
